import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Megaphone,
  Wallet,
  Eye,
  Radio,
  Target,
  ArrowRight,
  Loader2,
  RefreshCcw,
  Lock,
  Sparkles,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Plus,
  Minus,
  LocateFixed,
  Calendar,
  ChevronDown,
  Check,
} from "lucide-react";
import { cn } from "../../../utils/function";
import { AiInsightsProvider, AskableValue, ExecutiveSummary, usePanelInsights } from "../ai-insights";
import useRequest from "../../../components/hook/use-req";
import { UserResponse } from "../../../components/types";
import { NO_PERFORMANCE_HEX, nigeriaOutlinePath, performanceLegend, projectLatLng, RegionPerformance, regionPerformance } from "../nigeria-map";

// Rebuilt against the real, now-live CXO Dashboard API (per
// cxo_dashboard_campaigns.postman_collection.json — "Campaigns, Ad sets and
// Creative Detail"). Response bodies aren't typed in the spec (empty
// schemas), so field names below are either confirmed by the collection's
// own test assertions/description prose or a reasonable best-effort reading
// of it — every number is read defensively and falls back to "—"/locked
// rather than guessing a shape that doesn't exist. Locked metrics follow one
// confirmed convention throughout this API: `{ available: false }`.
//
// Regional Performance (GET /cxo-dashboard/regions) is real now — confirmed
// live table shape is spend/reach/results/reported_value/roas/campaigns run
// plus lat/lng for the Birds Eye markers; there's no growth_status field at
// all (an earlier version of the doc implied one); marker/badge colour comes
// from each region's own backend `performance` rating + hex instead, same as
// the Campaigns > Regions tab (see regionPerformance in nigeria-map.ts). Per direct ask,
// the map itself is a lightweight custom visual (no new mapping dependency/
// GeoJSON) — markers are positioned from real lat/lng against Nigeria's real
// bounding box, but the background isn't a geographically accurate coastline.
//
// Still not in this API at all: Budget Utilisation, Total Ad Spend vs
// Attributed Revenue, and the Forecaster section's Revenue Pacing panels
// (only "Actual vs Historical" has a real endpoint — performance/cumulative
// — not wired up yet, out of scope for this pass). These stay LockedPanel.
//
// The "Reach 18K/24K vs target" bars in the reference Chart view for Top Ad
// Campaigns imply a reach *target* this API never returns (its own
// description says so explicitly) — rather than inventing one, this Chart
// view's bar length is each campaign's spend relative to the top campaign
// shown, a real, honest number instead of a fabricated target.

type DateRange = "Last 24 Hours" | "Last 7 Days" | "Last 30 Days" | "Quarter to date";
const DATE_RANGES: DateRange[] = ["Last 24 Hours", "Last 7 Days", "Last 30 Days", "Quarter to date"];
const fmtLabel = (d: Date) => d.toLocaleDateString(undefined, { day: "numeric", month: "long" });

const fmtDate = (d: Date) => d.toISOString().slice(0, 10);
// The collection's own `mode=fixed` examples use start/end dates directly
// (no preset enum) — this UI keeps the familiar preset picker and converts
// it to real dates client-side.
function presetToDates(preset: DateRange): { start_date: string; end_date: string } {
  const end = new Date();
  const start = new Date();
  if (preset === "Last 24 Hours") start.setDate(end.getDate() - 1);
  else if (preset === "Last 7 Days") start.setDate(end.getDate() - 7);
  else if (preset === "Last 30 Days") start.setDate(end.getDate() - 30);
  else start.setMonth(Math.floor(end.getMonth() / 3) * 3, 1);
  return { start_date: fmtDate(start), end_date: fmtDate(end) };
}

// A metric may come back as a bare number or as `{value, change_pct}` (the
// shape this dashboard's previous data source used, and the Figma's own
// "+30 vs Last Period" deltas imply this API follows the same convention) —
// read both without assuming either is the real one.
//
// Confirmed live (crashed the page — "Objects are not valid as a React
// child... {value, indicator}"): some plain-looking fields (e.g. a campaign's
// `outcome`) are actually `{value, indicator}` objects, not bare
// strings/numbers — a third real shape on top of the two this already
// handled. unwrapValue is the single place that peels any of these back to
// a primitive; every formatter and every raw-rendered field below goes
// through it first so a shape this hasn't seen yet shows "—" instead of
// crashing the page again.
function unwrapValue(raw: any): any {
  if (raw == null) return raw;
  if (typeof raw === "object" && !Array.isArray(raw) && "value" in raw) return raw.value;
  return raw;
}

function readMetric(raw: any): { value: number | null; delta: number | null; available: boolean } {
  if (raw == null) return { value: null, delta: null, available: false };
  if (typeof raw === "object" && raw.available === false) return { value: null, delta: null, available: false };
  if (typeof raw === "object") {
    const value = unwrapValue(raw) ?? raw.total ?? null;
    const delta = raw.change_pct ?? raw.delta_pct ?? raw.change ?? null;
    return { value, delta, available: value != null };
  }
  return { value: raw, delta: null, available: true };
}

// `empty` lets a specific call site opt into "0"/"N/A" instead of the
// default "—" (e.g. the Regional Performance table, per direct ask) without
// changing every other place these are used across the page.
const formatCount = (n: any, empty: string = "—") => {
  const v = unwrapValue(n);
  return v == null || isNaN(Number(v)) ? empty : Math.round(Number(v)).toLocaleString();
};
const formatCurrency = (n: any, empty: string = "—") => {
  const v = unwrapValue(n);
  return v == null || isNaN(Number(v)) ? empty : `₦${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};
const formatDelta = (pct: number | null) => (pct == null ? null : `${pct > 0 ? "+" : ""}${pct.toFixed(1)}%`);
const asText = (raw: any): string => {
  const v = unwrapValue(raw);
  return v == null ? "—" : String(v);
};

const LockedStatCard: React.FC<{ icon: React.ComponentType<any>; label: string; description: string }> = ({ icon: Icon, label, description }) => (
  <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4">
    <div className="flex items-center justify-between text-[12px] font-semibold text-gray-500 dark:text-gray-100 mb-2">
      <span className="flex items-center gap-1.5 truncate">
        <Lock size={11} className="text-gray-400 dark:text-gray-500 shrink-0" />
        {label}
      </span>
      <Icon size={14} className="text-gray-300 dark:text-gray-600 shrink-0" />
    </div>
    <p className="text-xl sm:text-2xl font-semibold text-gray-300 dark:text-gray-700 blur-[5px] select-none">₦0.00</p>
    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5 leading-snug">{description}</p>
  </div>
);

const ComingSoonStatCard: React.FC<{ label: string; description: string }> = ({ label, description }) => (
  <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4 flex flex-col">
    <div className="flex items-center justify-between text-[12px] font-semibold text-gray-500 dark:text-gray-100 mb-2">
      <span className="flex items-center gap-1.5 truncate">
        <Lock size={11} className="text-gray-400 dark:text-gray-500 shrink-0" />
        {label}
      </span>
      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400 shrink-0">
        Coming Soon
      </span>
    </div>
    <div className="flex-1 flex items-center justify-center py-1.5">
      <Sparkles size={26} className="text-indigo-400 dark:text-indigo-500" />
    </div>
    <p className="text-xs text-gray-400 dark:text-gray-500 leading-snug">{description}</p>
  </div>
);

// Per the updated design: every locked insight now reads the same bold
// title ("Available in Upcoming Release") with a lock icon in a dark rounded
// square to its left, rather than each card inventing its own "X will be
// available soon" headline — only the detail line underneath varies per card.
const UpcomingReleaseCard: React.FC<{ detail: string }> = ({ detail }) => (
  <div className="flex items-start gap-4 rounded-xl bg-[#FAF9F5] dark:bg-gray-800/60 p-4 sm:p-5">
    <div className="shrink-0 flex items-center justify-center w-11 h-11 rounded-xl bg-gray-900 dark:bg-gray-700">
      <Lock size={18} className="text-white" />
    </div>
    <div>
      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Available in Upcoming Release</p>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 leading-snug">{detail}</p>
    </div>
  </div>
);

const LockedPanel: React.FC<{
  title: string;
  description?: string;
  detail: string;
}> = ({ title, description, detail }) => (
  <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4 sm:p-5">
    <div className="flex items-center justify-between mb-1">
      <h2 className="flex items-center gap-1.5 text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">
        <Lock size={14} className="text-gray-400 dark:text-gray-500 shrink-0" />
        {title}
      </h2>
    </div>
    {description && <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">{description}</p>}
    <UpcomingReleaseCard detail={detail} />
  </div>
);

const NotConnectedPanel: React.FC<{ title: string; description: string; note: string }> = ({ title, description, note }) => (
  <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4 sm:p-5">
    <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 mb-4">{description}</p>
    <div className="flex flex-col items-center justify-center text-center py-10 text-gray-400 dark:text-gray-500">
      <p className="text-sm font-medium">{note}</p>
      <p className="text-xs mt-1">The backend will start returning real data here once it's wired up.</p>
    </div>
  </div>
);

// Same dropdown used for the date-range picker on the Ad Creative Performance
// page (ads-creative-performance/index.tsx) — kept identical rather than a
// second copy with its own drift.
const CheckDropdown: React.FC<{ value: string; options: string[]; onChange: (v: string) => void; icon?: React.ReactNode }> = ({ value, options, onChange, icon }) => {
  const [open, setOpen] = useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="flex items-center gap-2 px-3.5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors whitespace-nowrap"
      >
        {icon}
        {value}
        <ChevronDown size={14} className={cn("text-gray-400 dark:text-gray-500 transition-transform", open ? "rotate-180" : "")} />
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 min-w-full w-max rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg overflow-hidden py-1">
          {options.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => {
                onChange(opt);
                setOpen(false);
              }}
              className="flex items-center gap-2 w-full text-left px-3.5 py-2 text-sm whitespace-nowrap hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
            >
              <Check size={14} className={cn("shrink-0", opt === value ? "text-amber-600 dark:text-amber-400" : "text-transparent")} />
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const STATUS_COLORS: Record<string, string> = {
  active: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400",
  paused: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
  ended: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
};

// Performance badge (table + map popup) — tinted from the backend's own
// `performance.hex` rather than a fixed Tailwind palette, so it always
// matches the marker colour; no rating → gray "N/A".
const PerformanceBadge: React.FC<{ perf: RegionPerformance | null; size?: "xs" | "sm" }> = ({ perf, size = "sm" }) => {
  const hex = perf?.hex ?? NO_PERFORMANCE_HEX;
  return (
    <span
      className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium", size === "xs" ? "text-[10px]" : "text-xs", !perf && "text-gray-600 dark:text-gray-300")}
      style={{ background: `${hex}1f`, color: perf ? hex : undefined }}
      title={perf?.vsAveragePct != null && perf.basis ? `${perf.vsAveragePct > 0 ? "+" : ""}${perf.vsAveragePct.toFixed(1)}% ${perf.basis} vs average` : undefined}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: hex }} />
      {perf?.label || "N/A"}
    </span>
  );
};

// A column-header sort toggle — shared by every sortable table on this page.
const SortHeader: React.FC<{ label: string; sortKey: string; active: string; order: "asc" | "desc"; onSort: (key: string) => void; align?: "left" | "right" }> = ({
  label,
  sortKey,
  active,
  order,
  onSort,
  align = "left",
}) => (
  <button
    type="button"
    onClick={() => onSort(sortKey)}
    className={cn("flex items-center gap-1 font-semibold hover:text-gray-600 dark:hover:text-gray-300", align === "right" && "ml-auto")}
  >
    {label}
    {active === sortKey ? order === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} /> : <ArrowUpDown size={11} className="text-gray-300 dark:text-gray-600" />}
  </button>
);

// Real Nigeria border outline + projector, shared with the Campaigns >
// Regions tab's own map (campaigns/tabs/regions-tab.tsx) — see nigeria-map.ts.
// Per direct ask: bigger again (was 320x280, then 430x350) to give the
// now-always-on labels (see showInlineLabels below) more room to spread out
// across all ~37 states. Kept close to Nigeria's real bounding-box ratio
// (~1.23, from NG_BOUNDS in nigeria-map.ts) rather than copying regions-tab's
// own ratio — that one is taller than Nigeria's real proportions, which would
// distort the shape further here; this card's container is full-width
// (unlike regions-tab's half-width column), so a wider-than-tall box both
// looks right and fills more of that width. The container is already full
// available width with no further headroom there (no max-width above it in
// this page's layout) — height is the lever that actually has room to grow.
const MAP_W = 610;
const MAP_H = 495;

interface RegionRow {
  name: string;
  lat: number | null;
  lng: number | null;
  spend: any;
  outcome: any;
  attributedRevenue: any;
  roas: any;
  campaigns: any;
  reach: any;
  ctr: any;
  performance: RegionPerformance | null;
}

// Confirmed live: the real per-item name field is `region`, not `name` —
// read it first rather than as a fallback. Only `region`/`lat`/`lng`/
// `campaigns` are top-level on the item; every actual metric (spend, results,
// attributed_revenue, roas, reach, ctr) is nested one level down under
// `metrics.<field>` as a real `{value, previous, change, change_pct}` object
// — this used to read those as flat top-level fields, which always came back
// undefined (only region/lat/lng/campaigns ever rendered, never a single
// metric) since the backend wrapped them in `metrics` without this being
// updated to match. `metrics` is read first; the old flat fields stay as a
// fallback in case an older cached response shows up without it.
function normalizeRegion(raw: any): RegionRow {
  const lat = unwrapValue(raw.lat);
  const lng = unwrapValue(raw.lng ?? raw.lon ?? raw.long);
  const m = raw.metrics || {};
  return {
    name: asText(raw.region ?? raw.name ?? raw.location),
    lat: lat == null || isNaN(Number(lat)) ? null : Number(lat),
    lng: lng == null || isNaN(Number(lng)) ? null : Number(lng),
    spend: m.spend ?? raw.spend,
    outcome: m.results ?? raw.outcome ?? raw.results,
    attributedRevenue: m.attributed_revenue ?? raw.attributed_revenue ?? raw.reported_value,
    roas: m.roas ?? raw.roas,
    campaigns: raw.campaigns ?? raw.campaigns_run ?? raw.campaign_count,
    reach: m.reach ?? raw.reach ?? raw.total_reach,
    ctr: m.ctr ?? raw.ctr,
    performance: regionPerformance(raw),
  };
}

const RegionsBirdsEye: React.FC<{ regions: RegionRow[] }> = ({ regions }) => {
  const [scale, setScale] = useState(1);
  const [selected, setSelected] = useState<RegionRow | null>(null);
  const placeable = regions.filter((r) => r.lat != null && r.lng != null);
  // Per direct ask: every marker always shows its name + results inline now,
  // regardless of count (previously gated off above 15 to avoid overlap at
  // the unscoped/tenant-wide view — that trade-off is overridden here; zoom
  // in to separate labels that overlap at the national level).
  const showInlineLabels = true;
  const { legend, basis } = performanceLegend(regions.map((r) => r.performance));

  return (
    <div>
      <div className="relative rounded-xl overflow-hidden bg-sky-50 dark:bg-gray-950" style={{ height: 850 }}>
        {/* Real Nigeria border outline (nigeria-map.ts) — not a geographically
            precise survey map, but a recognizable national silhouette rather
            than an abstract gradient backdrop. Markers below are projected
            through the exact same bounds as the outline, so they land
            consistently relative to the drawn coastline/borders. */}
        <svg viewBox={`0 0 ${MAP_W} ${MAP_H}`} className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid meet">
          <defs>
            <clipPath id="nigeriaClip">
              <path d={nigeriaOutlinePath(MAP_W, MAP_H)} />
            </clipPath>
            <radialGradient id="terrainGreen" cx="50%" cy="50%" r="60%">
              <stop offset="0%" stopColor="#86efac" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#86efac" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="terrainAmber" cx="50%" cy="50%" r="60%">
              <stop offset="0%" stopColor="#fde68a" stopOpacity="0.55" />
              <stop offset="100%" stopColor="#fde68a" stopOpacity="0" />
            </radialGradient>
          </defs>
          {/* The whole map — terrain, border AND markers — scales together as
              one unit now (previously only the markers group had the `scale`
              transform, so zooming in spread the dots away from the center
              while the outline stayed fixed size, pushing markers outside the
              drawn shape). Keeping them in one `<g>` means a marker's
              projected (x, y) stays correctly anchored inside the outline at
              any zoom level. */}
          <g style={{ transform: `scale(${scale})`, transformOrigin: "50% 50%", transition: "transform 150ms ease" }}>
            {/* Decorative terrain texture + rivers, clipped to the real outline
                above so nothing spills into the surrounding water — a visual
                fill, not a geographically accurate vegetation/hydrology map. */}
            <g clipPath="url(#nigeriaClip)">
              <rect x={0} y={0} width={MAP_W} height={MAP_H} className="fill-emerald-50 dark:fill-emerald-950/50" />
              <ellipse cx={MAP_W * 0.32} cy={MAP_H * 0.3} rx={100} ry={80} fill="url(#terrainGreen)" />
              <ellipse cx={MAP_W * 0.68} cy={MAP_H * 0.62} rx={110} ry={90} fill="url(#terrainAmber)" />
              <ellipse cx={MAP_W * 0.48} cy={MAP_H * 0.82} rx={90} ry={60} fill="url(#terrainGreen)" />
              {/* Niger + Benue rivers, roughly — illustrative curves, not surveyed hydrology. */}
              <path d="M 70,40 C 110,90 95,140 130,170 C 160,195 180,210 185,230" className="stroke-sky-300 dark:stroke-sky-700" strokeWidth={2} fill="none" opacity={0.7} />
              <path d="M 185,230 C 230,225 260,215 300,205" className="stroke-sky-300 dark:stroke-sky-700" strokeWidth={1.6} fill="none" opacity={0.6} />
            </g>
            <path d={nigeriaOutlinePath(MAP_W, MAP_H)} fill="none" className="stroke-emerald-500 dark:stroke-emerald-600" strokeWidth={1.5} />
            {placeable.map((r, i) => {
              const { x, y } = projectLatLng(r.lat!, r.lng!, MAP_W, MAP_H, 8);
              const dot = r.performance?.hex ?? NO_PERFORMANCE_HEX;
              const results = unwrapValue(r.outcome);
              return (
                <g key={r.name + i}>
                  <foreignObject x={x - 10} y={y - 10} width={20} height={20} style={{ overflow: "visible" }}>
                    <button
                      type="button"
                      onClick={() => setSelected(selected?.name === r.name ? null : r)}
                      className="relative flex items-center justify-center w-5 h-5 cursor-pointer"
                      title={r.name}
                    >
                      <span className="absolute inline-flex h-full w-full rounded-full opacity-40 animate-ping" style={{ background: dot }} />
                      <span className="relative inline-flex w-3 h-3 rounded-full border-2 border-white dark:border-gray-900 shadow" style={{ background: dot }} />
                    </button>
                  </foreignObject>
                  {/* Same adaptive labeling as the Campaigns Regions tab — see
                      showInlineLabels above. */}
                  {showInlineLabels && (
                    <g onClick={() => setSelected(selected?.name === r.name ? null : r)} className="cursor-pointer">
                      <text x={x + 10} y={y - 4} fontSize={9} fontWeight={600} className="fill-gray-700 dark:fill-gray-200">
                        {r.name}
                      </text>
                      <text x={x + 10} y={y + 7} fontSize={8} className="fill-gray-500 dark:fill-gray-400">
                        {results != null ? `${formatCount(results)} results` : ""}
                      </text>
                    </g>
                  )}
                </g>
              );
            })}
          </g>
        </svg>

        {/* Popup card for whichever marker is selected — same fields as the
            table row, read defensively (see normalizeRegion). */}
        {selected && (
          <div className="absolute top-3 left-3 w-60 bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-100 dark:border-gray-800 p-3.5 text-xs">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{selected.name}, Nigeria</p>
              <button type="button" onClick={() => setSelected(null)} className="text-gray-300 hover:text-gray-500 dark:hover:text-gray-300 text-sm leading-none">
                ×
              </button>
            </div>
            <div className="space-y-1.5 text-gray-600 dark:text-gray-300">
              <div className="flex justify-between"><span>Spend</span><span className="font-medium text-gray-900 dark:text-gray-100">{formatCurrency(selected.spend, "₦0.00")}</span></div>
              <div className="flex justify-between"><span>Campaigns Run</span><span className="font-medium text-gray-900 dark:text-gray-100">{formatCount(selected.campaigns, "0")}</span></div>
              <div className="flex justify-between"><span>Total Reach</span><span className="font-medium text-gray-900 dark:text-gray-100">{formatCount(selected.reach, "0")}</span></div>
              <div className="flex justify-between"><span>CTR</span><span className="font-medium text-gray-900 dark:text-gray-100">{(() => { const v = unwrapValue(selected.ctr); return v != null && !isNaN(Number(v)) ? `${Number(v).toFixed(1)}%` : "0%"; })()}</span></div>
              <div className="flex justify-between"><span>ROAS</span><span className="font-medium text-gray-900 dark:text-gray-100">{(() => { const v = unwrapValue(selected.roas); return typeof v === "number" ? `${v.toFixed(1)}x` : "N/A"; })()}</span></div>
              <div className="flex justify-between"><span>Attributed Revenue</span><span className="font-medium text-gray-900 dark:text-gray-100">{formatCurrency(selected.attributedRevenue, "₦0.00")}</span></div>
              <div className="flex justify-between items-center pt-1">
                <span>Performance</span>
                <PerformanceBadge perf={selected.performance} size="xs" />
              </div>
            </div>
          </div>
        )}

        {/* Legend — built from the ratings actually in the response. */}
        {legend.length > 0 && (
          <div className="absolute bottom-3 left-3 bg-white/90 dark:bg-gray-900/90 rounded-lg px-3 py-2 text-[11px] text-gray-600 dark:text-gray-300 space-y-1">
            {legend.map((p) => (
              <div key={p.rating} className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full" style={{ background: p.hex }} />
                {p.label}
              </div>
            ))}
            {basis && <div className="text-gray-400 dark:text-gray-500 pt-0.5">Rated on {basis} vs the regional average</div>}
          </div>
        )}

        {/* Zoom controls */}
        <div className="absolute bottom-3 right-3 flex flex-col gap-1">
          <button type="button" onClick={() => setScale((s) => Math.min(2.5, s + 0.25))} className="w-7 h-7 flex items-center justify-center bg-white dark:bg-gray-900 rounded-lg shadow border border-gray-100 dark:border-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800">
            <Plus size={13} />
          </button>
          <button type="button" onClick={() => setScale((s) => Math.max(1, s - 0.25))} className="w-7 h-7 flex items-center justify-center bg-white dark:bg-gray-900 rounded-lg shadow border border-gray-100 dark:border-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800">
            <Minus size={13} />
          </button>
          <button type="button" onClick={() => setScale(1)} className="w-7 h-7 flex items-center justify-center bg-white dark:bg-gray-900 rounded-lg shadow border border-gray-100 dark:border-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800" title="Reset view">
            <LocateFixed size={13} />
          </button>
        </div>
      </div>
      {placeable.length === 0 && regions.length > 0 && (
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">None of these regions have a placeable location yet — showing the table instead is more useful until they do.</p>
      )}
    </div>
  );
};

const CXODashboard: React.FC = () => {
  const navigate = useNavigate();
  // Real date-range picker, same preset list and pattern as the Ad Creative
  // Performance page — only meaningful in "Fixed Period" mode (realtime has
  // no range to pick).
  const [dateRange, setDateRange] = useState<DateRange>("Last 7 Days");
  const [liveMode, setLiveMode] = useState<"realtime" | "fixed">("fixed");

  const token = localStorage.getItem("authToken");
  const authHeaders = { Authorization: `Bearer ${token}` };
  const [user, setUser] = useState<UserResponse | null>(null);
  const { makeRequest: getUserData } = useRequest(`/auth/get_userdata`, "GET", authHeaders);
  useEffect(() => {
    const fetchUser = async () => {
      const [response] = await getUserData();
      setUser(response);
    };
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { start_date, end_date } = useMemo(() => presetToDates(dateRange), [dateRange]);
  const dateRangeLabel = useMemo(() => `${fmtLabel(new Date(start_date))} – ${fmtLabel(new Date(end_date))}`, [start_date, end_date]);
  const baseParams = useMemo(
    () => (liveMode === "realtime" ? { mode: "realtime" } : { mode: "fixed", start_date, end_date }),
    [liveMode, start_date, end_date]
  );

  const [overview, setOverview] = useState<Record<string, any> | null>(null);
  const [loadError, setLoadError] = useState(false);
  const { makeRequest: getOverview, loading } = useRequest("/cxo-dashboard/overview", "GET", authHeaders);

  const [attribution, setAttribution] = useState<Record<string, any> | null>(null);
  const { makeRequest: getAttribution } = useRequest("/cxo-dashboard/attribution-overview", "GET", authHeaders);
  const [funnel, setFunnel] = useState<Record<string, any> | null>(null);
  const { makeRequest: getFunnel } = useRequest("/cxo-dashboard/conversion-funnel", "GET", authHeaders);
  const [subscriptions, setSubscriptions] = useState<Record<string, any> | null>(null);
  const { makeRequest: getSubscriptions } = useRequest("/cxo-dashboard/subscriptions", "GET", authHeaders);
  const [products, setProducts] = useState<Record<string, any> | null>(null);
  const { makeRequest: getProducts } = useRequest("/cxo-dashboard/subscriptions/products", "GET", authHeaders);
  const [topCampaigns, setTopCampaigns] = useState<any[]>([]);
  const { makeRequest: getTopCampaigns } = useRequest("/cxo-dashboard/campaigns/top", "GET", authHeaders);
  const [campaignsView, setCampaignsView] = useState<"table" | "chart">("table");

  // Real GET /cxo-dashboard/regions — its own sort/order, independent of the
  // rest of the dashboard's refresh, so re-sorting the table doesn't refetch
  // everything else on the page.
  const [regions, setRegions] = useState<RegionRow[]>([]);
  const [regionsLoading, setRegionsLoading] = useState(true);
  const [regionView, setRegionView] = useState<"table" | "birdseye">("birdseye");
  const [regionSort, setRegionSort] = useState<{ by: string; order: "asc" | "desc" }>({ by: "spend", order: "desc" });
  const { makeRequest: getRegions } = useRequest("/cxo-dashboard/regions", "GET", authHeaders);
  const fetchRegions = React.useCallback(async () => {
    setRegionsLoading(true);
    const [res] = await getRegions(undefined, { sort_by: regionSort.by, order: regionSort.order, limit: "50", offset: "0", ...baseParams });
    setRegionsLoading(false);
    setRegions(Array.isArray(res?.items) ? res.items.map(normalizeRegion) : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(baseParams), regionSort.by, regionSort.order]);
  useEffect(() => {
    fetchRegions();
  }, [fetchRegions]);
  const handleRegionSort = (key: string) =>
    setRegionSort((prev) => (prev.by === key ? { by: key, order: prev.order === "asc" ? "desc" : "asc" } : { by: key, order: "desc" }));

  const fetchDashboard = React.useCallback(async () => {
    setLoadError(false);
    // ai_insights=false keeps this fast; the AI cards load separately below.
    const [ov, ovStatus] = await getOverview(undefined, { ...baseParams, ai_insights: "false" });
    if (ovStatus === 200 && ov?.status) {
      setOverview(ov.data || ov);
    } else {
      setLoadError(true);
      toast.error(ov?.detail || ov?.message || "Failed to load the CXO dashboard.");
    }
    const [attr] = await getAttribution(undefined, { range: "last_week" });
    setAttribution(attr?.data || attr || null);
    const [fun] = await getFunnel(undefined, { range: "last_week" });
    setFunnel(fun?.data || fun || null);
    const [subs] = await getSubscriptions(undefined, baseParams);
    setSubscriptions(subs?.data || subs || null);
    const [prod] = await getProducts(undefined, { product: "all", ...baseParams });
    setProducts(prod?.data || prod || null);
    const [camp] = await getTopCampaigns(undefined, { sort_by: "spend", order: "desc", limit: "10", offset: "0", ...baseParams });
    setTopCampaigns(Array.isArray(camp?.items) ? camp.items : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(baseParams)]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const aiInsights = usePanelInsights({ overview: { endpoint: "/cxo-dashboard/overview", params: baseParams } });

  const handleModeChange = (mode: "realtime" | "fixed") => setLiveMode(mode);

  const campaigns = readMetric(overview?.total_ad_campaigns);
  const spend = readMetric(overview?.total_campaign_spend);
  const impressions = readMetric(overview?.total_impressions);
  const reach = readMetric(overview?.total_reach);
  const costPerResult = readMetric(overview?.avg_cost_per_result);
  const attributedRevenue = readMetric(overview?.attributed_revenue);
  const roas = readMetric(overview?.roas);

  const CAMPAIGN_OVERVIEW_CARDS = [
    { key: "campaigns", icon: Megaphone, label: "Total Ad Campaigns", metric: campaigns, format: formatCount, description: "Total campaigns you have run during the selected period.", path: "/cxo/campaigns" },
    { key: "spend", icon: Wallet, label: "Total Campaign Spend", metric: spend, format: formatCurrency, description: "Total amount spent across all social campaigns and channels." },
    { key: "impressions", icon: Eye, label: "Total Impressions", metric: impressions, format: formatCount, description: "Total number of times your campaigns were displayed in the selected period." },
    { key: "reach", icon: Radio, label: "Total Reach", metric: reach, format: formatCount, description: "Unique audience reached across all campaigns and channels in the selected period." },
    { key: "cost_per_result", icon: Target, label: "Avg. Cost Per Result", metric: costPerResult, format: formatCurrency, description: "Average spend required to generate one campaign result." },
  ];

  const attributionLocked = !attribution || attribution.available === false;
  const funnelLocked = !funnel || funnel.available === false;
  const appSubs = readMetric(subscriptions?.app_subscriptions);
  const costPerSub = readMetric(subscriptions?.cost_per_subscription);
  const productsLocked = !products || products.available === false;

  const compareLabel = "last period";

  if (!overview && loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F5] dark:bg-gray-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-gray-400 dark:text-gray-500">
          <Loader2 size={22} className="animate-spin" />
          <p className="text-sm">Loading your CXO dashboard…</p>
        </div>
      </div>
    );
  }

  if (!overview && loadError) {
    return (
      <div className="min-h-screen bg-[#FAF9F5] dark:bg-gray-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-gray-500 dark:text-gray-400 text-center px-6">
          <p className="text-sm font-medium">Couldn't load the CXO dashboard.</p>
          <button
            type="button"
            onClick={fetchDashboard}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg"
          >
            <RefreshCcw size={14} /> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("min-h-screen bg-[#FAF9F5] dark:bg-gray-950 p-4 sm:p-6 transition-opacity", loading && "opacity-60")}>
      <div className="space-y-5">
        {/* Greeting + Real-Time/Fixed Period toggle */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100">
            Here's what's happening{user?.payload?.first_name ? `, ${user.payload.first_name}` : ""}.
          </h1>
          <div className="flex items-center gap-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-1 shrink-0">
            {(["realtime", "fixed"] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => handleModeChange(mode)}
                className={cn(
                  "px-3.5 py-1.5 text-sm font-medium rounded-lg transition-colors",
                  liveMode === mode
                    ? "bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900"
                    : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                )}
              >
                {mode === "realtime" ? "Real-Time" : "Fixed Period"}
              </button>
            ))}
          </div>
        </div>

        {/* Growth & Campaign Impact */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-gray-100">Growth &amp; Campaign Impact</h2>
            <p className="text-sm text-gray-500 dark:text-white mt-0.5 font-medium max-w-xl">
              Monitor how ad campaign spend translates into revenue, subscriptions, and measurable business outcomes.
            </p>
          </div>
          {liveMode === "fixed" && (
            <CheckDropdown value={dateRangeLabel} options={DATE_RANGES} onChange={(v) => setDateRange(v as DateRange)} icon={<Calendar size={14} className="text-gray-400 dark:text-gray-500" />} />
          )}
        </div>

        {/* Campaign Overview */}
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Campaign Overview</h3>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
            {CAMPAIGN_OVERVIEW_CARDS.map((c) => {
              const Icon = c.icon;
              const delta = formatDelta(c.metric.delta);
              return (
                <div key={c.key} className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4">
                  <div className="flex items-center justify-between text-[12px] font-semibold text-gray-500 dark:text-gray-100 mb-2">
                    <span className="truncate">{c.label}</span>
                    <Icon size={14} className="text-gray-400 dark:text-gray-500 shrink-0" />
                  </div>
                  {(c as any).path ? (
                    <button
                      type="button"
                      onClick={() => navigate((c as any).path)}
                      className={cn("text-xl sm:text-2xl font-semibold hover:underline", c.metric.value == null ? "text-gray-300 dark:text-gray-700" : "text-gray-900 dark:text-gray-100")}
                    >
                      {c.format(c.metric.value)}
                    </button>
                  ) : (
                    <p className={cn("text-xl sm:text-2xl font-semibold", c.metric.value == null ? "text-gray-300 dark:text-gray-700" : "text-gray-900 dark:text-gray-100")}>
                      {c.metric.value == null ? c.format(c.metric.value) : <AskableValue label={c.label} value={c.format(c.metric.value)} />}
                    </p>
                  )}
                  {delta ? (
                    <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1.5 flex items-center gap-1">
                      <ArrowRight size={11} className="-rotate-45" /> {delta} vs {compareLabel}
                    </p>
                  ) : (
                    <p className="text-xs text-gray-300 dark:text-gray-600 mt-1.5">{c.metric.value == null ? "Not available yet" : "No comparison data"}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <ExecutiveSummary period={liveMode === "fixed" ? dateRangeLabel : "Real-time"} insight={aiInsights.overview} />

        {/* Attributed Revenue / ROAS / Attribution Confidence */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {attributedRevenue.available ? (
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4">
              <div className="flex items-center justify-between text-[12px] font-semibold text-gray-500 dark:text-gray-100 mb-2">
                <span className="truncate">Attributed Revenue</span>
                <Wallet size={14} className="text-gray-400 dark:text-gray-500 shrink-0" />
              </div>
              <p className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(attributedRevenue.value)}</p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">Revenue linked back to campaign activity within the selected period.</p>
            </div>
          ) : (
            <LockedStatCard icon={Wallet} label="Attributed Revenue" description="Revenue linked back to campaign activity within the selected period." />
          )}
          {roas.available ? (
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4">
              <div className="flex items-center justify-between text-[12px] font-semibold text-gray-500 dark:text-gray-100 mb-2">
                <span className="truncate">ROAS</span>
                <Target size={14} className="text-gray-400 dark:text-gray-500 shrink-0" />
              </div>
              <p className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100">{typeof roas.value === "number" ? `${roas.value.toFixed(1)}x` : roas.value != null ? asText(roas.value) : "—"}</p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">Revenue generated for every ₦1 spent on campaigns.</p>
            </div>
          ) : (
            <LockedStatCard icon={Target} label="ROAS" description="Revenue generated for every ₦1 spent on campaigns." />
          )}
          <ComingSoonStatCard label="Attribution Confidence" description="Understand how reliably campaign activity can be linked to business outcomes." />
        </div>

        {/* Attribution Overview + Conversion Funnel — both confirmed locked
            live today (GET .../attribution-overview and .../conversion-funnel
            both return available: false), checked live each load rather than
            hardcoded so this flips automatically once the backend unlocks
            either one. */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {attributionLocked ? (
            <LockedPanel
              title="Attribution Overview"
              description="Understand how conversions are attributed across campaign touchpoints."
              detail="See how impressions, assisted conversions and attribution windows connect across campaigns."
            />
          ) : (
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4 sm:p-5">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">Attribution Overview</h2>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Understand how conversions are attributed across campaign touchpoints.</p>
              <pre className="mt-3 text-xs text-gray-500 dark:text-gray-400 overflow-x-auto">{JSON.stringify(attribution, null, 2)}</pre>
            </div>
          )}
          {funnelLocked ? (
            <LockedPanel
              title="Conversion Funnel"
              description="See how customers move from campaign reach to completed conversions."
              detail="See how customers move from campaign reach to completed conversions."
            />
          ) : (
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4 sm:p-5">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">Conversion Funnel</h2>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">See how customers move from campaign reach to completed conversions.</p>
              <pre className="mt-3 text-xs text-gray-500 dark:text-gray-400 overflow-x-auto">{JSON.stringify(funnel, null, 2)}</pre>
            </div>
          )}
        </div>

        {/* Subscriptions Overview */}
        <div>
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Subscriptions Overview</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            {appSubs.available ? (
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4">
                <div className="flex items-center justify-between text-[12px] font-semibold text-gray-500 dark:text-gray-100 mb-2">
                  <span className="truncate">App Subscriptions</span>
                  <Target size={14} className="text-gray-400 dark:text-gray-500 shrink-0" />
                </div>
                <p className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100">{formatCount(appSubs.value)}</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">New app subscriptions attributed to campaign activity from recipients.</p>
              </div>
            ) : (
              <LockedStatCard icon={Target} label="App Subscriptions" description="New app subscriptions attributed to campaign activity from recipients." />
            )}
            {costPerSub.available ? (
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4">
                <div className="flex items-center justify-between text-[12px] font-semibold text-gray-500 dark:text-gray-100 mb-2">
                  <span className="truncate">Cost Per Subscription</span>
                  <Wallet size={14} className="text-gray-400 dark:text-gray-500 shrink-0" />
                </div>
                <p className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(costPerSub.value)}</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">Average campaign spend required to generate one app subscription.</p>
              </div>
            ) : (
              <LockedStatCard icon={Wallet} label="Cost Per Subscription" description="Average campaign spend required to generate one app subscription." />
            )}
          </div>
          {productsLocked ? (
            <NotConnectedPanel
              title="Product Subscriptions"
              description="See which products are driving the most new subscriptions and how customers are converting."
              note="Product subscription breakdowns aren't connected yet."
            />
          ) : (
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4 sm:p-5">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">Product Subscriptions</h2>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">See which products are driving the most new subscriptions and how customers are converting.</p>
              <pre className="mt-3 text-xs text-gray-500 dark:text-gray-400 overflow-x-auto">{JSON.stringify(products, null, 2)}</pre>
            </div>
          )}
        </div>

        {/* Campaigns and Performance — real now (GET /cxo-dashboard/campaigns/top).
            Column names beyond `roas.available` aren't confirmed by a live
            response yet, so each cell reads a couple of plausible keys and
            falls back to "—" rather than guessing wrong silently. */}
        <div>
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Campaigns and Performance</h2>
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4 sm:p-5">
            <div className="flex items-center justify-between mb-0.5">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Top Ad Campaigns</h3>
              <div className="flex items-center gap-1 bg-gray-50 dark:bg-gray-800 rounded-lg p-1 shrink-0">
                {(["table", "chart"] as const).map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setCampaignsView(v)}
                    className={cn(
                      "px-3 py-1 text-xs font-medium rounded-md capitalize transition-colors",
                      campaignsView === v ? "bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900" : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                    )}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 mb-4">These are your top performing ad campaigns.</p>
            {topCampaigns.length === 0 ? (
              <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-8">No campaign performance data for this period yet.</p>
            ) : campaignsView === "table" ? (
              <div className="overflow-x-auto -mx-1">
                <table className="w-full min-w-[640px] text-sm">
                  <thead>
                    <tr className="text-left text-[11px] font-semibold text-gray-400 dark:text-gray-500 tracking-wide">
                      <th className="font-semibold pb-2 pl-1">CAMPAIGN</th>
                      <th className="font-semibold pb-2">STATUS</th>
                      <th className="font-semibold pb-2">SPEND</th>
                      <th className="font-semibold pb-2">REACH</th>
                      <th className="font-semibold pb-2">OUTCOME</th>
                      <th className="font-semibold pb-2">CTR</th>
                      <th className="font-semibold pb-2 text-right pr-1">ROAS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topCampaigns.map((item, i) => {
                      const roasMetric = readMetric(item.roas);
                      const statusText = asText(item.status);
                      const statusKey = statusText.toLowerCase();
                      const ctrValue = unwrapValue(item.ctr);
                      const campaignId = item.campaign_id || item.id;
                      return (
                        <tr key={campaignId || i} className="border-t border-gray-50 dark:border-gray-800">
                          <td className="py-2.5 pl-1 whitespace-nowrap">
                            {campaignId ? (
                              <button type="button" onClick={() => navigate(`/cxo/campaigns/${campaignId}`)} className="font-medium text-indigo-600 dark:text-indigo-400 hover:underline text-left">
                                {asText(item.name ?? item.campaign_name)}
                              </button>
                            ) : (
                              <span className="font-medium text-gray-800 dark:text-gray-100">{asText(item.name ?? item.campaign_name)}</span>
                            )}
                          </td>
                          <td className="py-2.5">
                            <span className={cn("px-2 py-0.5 rounded-full text-xs font-medium", STATUS_COLORS[statusKey] || "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300")}>
                              {statusText}
                            </span>
                          </td>
                          <td className="py-2.5 text-gray-600 dark:text-gray-300">{formatCurrency(item.spend, "₦0.00")}</td>
                          <td className="py-2.5 text-gray-600 dark:text-gray-300">{formatCount(item.reach, "0")}</td>
                          <td className="py-2.5 text-gray-600 dark:text-gray-300">{formatCount(item.outcome, "0")}</td>
                          <td className="py-2.5 text-gray-600 dark:text-gray-300">{ctrValue != null && !isNaN(Number(ctrValue)) ? `${Number(ctrValue).toFixed(1)}%` : "0%"}</td>
                          <td className="py-2.5 pr-1 text-right">
                            {roasMetric.available ? (
                              <span className="font-semibold text-emerald-600 dark:text-emerald-400">{typeof roasMetric.value === "number" ? `${roasMetric.value.toFixed(1)}x` : roasMetric.value != null ? asText(roasMetric.value) : "N/A"}</span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-gray-300 dark:text-gray-700 blur-[3px] select-none">
                                <Lock size={10} className="text-gray-300 dark:text-gray-700" /> 3.4x
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              // Chart view — bar length is each campaign's spend relative to
              // the top campaign shown here, a real ranking signal. The
              // reference design's bars imply a reach *target* this API never
              // returns (its own description says so explicitly), so this
              // doesn't invent one — see file header.
              (() => {
                const maxSpend = Math.max(...topCampaigns.map((c) => Number(unwrapValue(c.spend)) || 0), 1);
                return (
                  <div className="space-y-4">
                    {topCampaigns.map((item, i) => {
                      const spendValue = Number(unwrapValue(item.spend)) || 0;
                      const pct = Math.max(4, Math.round((spendValue / maxSpend) * 100));
                      const ctrValue = unwrapValue(item.ctr);
                      const cpaMetric = readMetric(item.cost_per_result ?? item.cpa);
                      const reachValue = unwrapValue(item.reach);
                      const campaignId = item.campaign_id || item.id;
                      return (
                        <div key={campaignId || i} className="flex items-center justify-between gap-4">
                          <div className="min-w-0 flex-1">
                            {campaignId ? (
                              <button type="button" onClick={() => navigate(`/cxo/campaigns/${campaignId}`)} className="text-sm font-medium text-indigo-600 dark:text-indigo-400 truncate hover:underline text-left block">
                                {asText(item.name ?? item.campaign_name)}
                              </button>
                            ) : (
                              <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{asText(item.name ?? item.campaign_name)}</p>
                            )}
                            <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden mt-1.5 max-w-xs">
                              <div className="h-full bg-gradient-to-r from-amber-300 to-amber-500 rounded-full" style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 shrink-0 whitespace-nowrap">
                            <span>CTR {ctrValue != null && !isNaN(Number(ctrValue)) ? `${Number(ctrValue).toFixed(1)}%` : "0%"}</span>
                            {/* <span>CPA {cpaMetric.value != null ? formatCurrency(cpaMetric.value) : "₦0.00"}</span> */}
                            <span>Reach {reachValue != null ? formatCount(reachValue) : "0"}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()
            )}
          </div>
        </div>

        {/* Regional Performance — real now (GET /cxo-dashboard/regions). */}
        <div>
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4 sm:p-5">
            <div className="flex items-center justify-between mb-0.5">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">Regional Performance</h2>
              <div className="flex items-center gap-1 bg-gray-50 dark:bg-gray-800 rounded-lg p-1 shrink-0">
                {([
                  { key: "birdseye", label: "Birds Eye" },
                  { key: "table", label: "Table" },
                ] as const).map((v) => (
                  <button
                    key={v.key}
                    type="button"
                    onClick={() => setRegionView(v.key)}
                    className={cn(
                      "px-3 py-1 text-xs font-medium rounded-md transition-colors",
                      regionView === v.key ? "bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900" : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                    )}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 mb-4">View where your best performing locations are.</p>
            {regionsLoading && regions.length === 0 ? (
              <p className="flex items-center gap-1.5 text-sm text-gray-400 dark:text-gray-500 py-8">
                <Loader2 size={14} className="animate-spin" /> Loading regional performance…
              </p>
            ) : regions.length === 0 ? (
              <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-8">No regional performance data for this period yet.</p>
            ) : regionView === "birdseye" ? (
              <RegionsBirdsEye regions={regions} />
            ) : (
              <div className="overflow-x-auto -mx-1">
                <table className="w-full min-w-[640px] text-sm">
                  <thead>
                    <tr className="text-left text-[11px] text-gray-400 dark:text-gray-500 tracking-wide">
                      <th className="pb-2 pl-1"><SortHeader label="LOCATION" sortKey="name" active={regionSort.by} order={regionSort.order} onSort={handleRegionSort} /></th>
                      <th className="pb-2"><SortHeader label="SPEND" sortKey="spend" active={regionSort.by} order={regionSort.order} onSort={handleRegionSort} /></th>
                      <th className="pb-2"><SortHeader label="OUTCOME" sortKey="outcome" active={regionSort.by} order={regionSort.order} onSort={handleRegionSort} /></th>
                      <th className="pb-2"><SortHeader label="ATTRIBUTED REVENUE" sortKey="attributed_revenue" active={regionSort.by} order={regionSort.order} onSort={handleRegionSort} /></th>
                      <th className="pb-2"><SortHeader label="ROAS" sortKey="roas" active={regionSort.by} order={regionSort.order} onSort={handleRegionSort} /></th>
                      <th className="pb-2 pr-1 text-right"><SortHeader label="PERFORMANCE" sortKey="roas" active={regionSort.by} order={regionSort.order} onSort={handleRegionSort} align="right" /></th>
                    </tr>
                  </thead>
                  <tbody>
                    {regions.map((r, i) => {
                      const roasValue = unwrapValue(r.roas);
                      return (
                        <tr key={r.name + i} className="border-t border-gray-50 dark:border-gray-800">
                          <td className="py-2.5 pl-1 font-medium text-indigo-600 dark:text-indigo-400 whitespace-nowrap hover:underline cursor-pointer">{r.name}</td>
                          <td className="py-2.5 text-gray-600 dark:text-gray-300">{formatCurrency(r.spend, "₦0.00")}</td>
                          <td className="py-2.5 text-gray-600 dark:text-gray-300">{formatCount(r.outcome, "0")}</td>
                          <td className="py-2.5 text-gray-600 dark:text-gray-300">{formatCurrency(r.attributedRevenue, "₦0.00")}</td>
                          <td className="py-2.5 text-gray-600 dark:text-gray-300">{typeof roasValue === "number" ? `${roasValue.toFixed(1)}x` : "N/A"}</td>
                          <td className="py-2.5 pr-1 text-right">
                            <PerformanceBadge perf={r.performance} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div>
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Budget and Revenue</h2>
          <div className="space-y-4">
            <LockedPanel
              title="Budget Utilisation"
              description="Track how much campaign budget has been used across the selected period."
              detail="Budget utilisation and pacing insights will be available in a future release."
            />
            <LockedPanel
              title="Total Ad Spends vs Attributed Revenue"
              description="Campaign spend vs revenue."
              detail="Spend, pacing and ROAS insights are planned for an upcoming release to help you track campaign efficiency and budget performance."
            />
          </div>
        </div>

        <div>
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Forecaster</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            <LockedPanel
              title="Actual vs Historical Performance"
              description="Cumulative revenue progression compared to the previous period."
              detail="Understand where revenue is heading and whether current momentum is enough to reach your target."
            />
            <LockedPanel
              title="Revenue Pacing Forecaster"
              description="Track broadcast momentum, revenue progression and forecast attainment across channels."
              detail="Suggested improvements for your ad spend, based on how campaigns are pacing toward your goals."
            />
          </div>
          <LockedPanel
            title="Revenue Pacing vs Historical Performance"
            description="Compare your current performance against historical periods."
            detail="Compare your current performance against historical periods to make better ad spend decisions."
          />
        </div>

        <p className="text-xs text-gray-400 dark:text-gray-500 text-center">All metrics compared to the {compareLabel}.</p>
      </div>
    </div>
  );
};

const CXODashboardPage: React.FC = () => (
  <AiInsightsProvider>
    <CXODashboard />
  </AiInsightsProvider>
);

export default CXODashboardPage;
